import React, { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CalendarClock, CheckCircle2, Video, ExternalLink, Loader2,
  ChevronLeft, ChevronRight, Clock, ArrowRight
} from "lucide-react";

const ConsultationBooking = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [zohoConfig, setZohoConfig] = useState(null);
  const [followupContext, setFollowupContext] = useState(null);
  const [followupError, setFollowupError] = useState(null);

  // URL params
  const bookingType = searchParams.get("type") || "initial";
  const followupToken = searchParams.get("token") || "";
  const accessToken = searchParams.get("access") || "";
  const weekNum = searchParams.get("week") || "";
  const [accessError, setAccessError] = useState(null);
  const isFollowUp = bookingType === "follow_up" && followupToken;

  // Booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(null);
  const scrollRef = useRef(null);

  // Bookable dates come from the server window: 24h minimum notice to 30 days ahead, weekdays, clinic timezone
  const [win, setWin] = useState(null);
  useEffect(() => { fetch(`${API}/booking/window`).then(r => r.json()).then(setWin).catch(() => {}); }, []);
  const lagosDate = (dt) => dt.toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
  const dates = [];
  if (win) {
    let d = new Date(`${lagosDate(new Date(win.earliest))}T12:00:00`);
    const last = new Date(`${win.latest_date}T12:00:00`);
    while (d <= last) {
      if (d.getDay() !== 0 && d.getDay() !== 6) dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const promises = [fetch(`${API}/booking/zoho-config`)];

        if (isFollowUp) {
          // Validate follow-up token server-side
          promises.push(
            fetch(`${API}/booking/validate-followup?token=${followupToken}${weekNum ? `&week=${weekNum}` : ""}`)
          );
        } else if (accessToken) {
          promises.push(fetch(`${API}/booking/access/${encodeURIComponent(accessToken)}`));
        } else if (user?.user_id) {
          promises.push(fetch(`${API}/booking/verify-access/${user.user_id}`));
        }

        const results = await Promise.all(promises);

        if (results[0]?.ok) setZohoConfig(await results[0].json());

        if (results[1]) {
          if (results[1].ok) {
            const data = await results[1].json();
            if (isFollowUp) {
              setFollowupContext(data);
              // If already booked, show it
              if (data.has_existing_booking) setAccess({ has_existing_booking: true, existing_booking: data.existing_booking });
            } else {
              setAccess(data);
            }
          } else {
            const err = await results[1].json().catch(() => ({}));
            if (isFollowUp) setFollowupError(err.detail || "This booking link is not valid.");
            else setAccessError(err.detail || "This booking link is not valid.");
          }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user, isFollowUp, followupToken, accessToken]);

  useEffect(() => {
    if (!selectedDate && dates.length > 0 && !loading) setSelectedDate(dates[0]);
  }, [loading, win]); // eslint-disable-line react-hooks/exhaustive-deps

  const [slotsMessage, setSlotsMessage] = useState("");
  const loadSlots = async (date, quiet = false) => {
    if (!quiet) { setSlotsLoading(true); setSelectedSlot(null); }
    try {
      const res = await fetch(`${API}/booking/available-slots?date=${lagosDate(date)}`);
      if (res.ok) { const d = await res.json(); setSlots(d.slots || []); setSlotsMessage(d.message || ""); return d.slots || []; }
    } catch (e) { console.error(e); }
    finally { if (!quiet) setSlotsLoading(false); }
    return null;
  };
  useEffect(() => { if (selectedDate) loadSlots(selectedDate); }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickSlot = async (slot) => {
    setSelectedSlot(slot);
    const fresh = await loadSlots(selectedDate, true);
    if (fresh && !fresh.some(x => x.datetime_utc === slot.datetime_utc)) {
      setSelectedSlot(null);
      toast.error("That time was just taken. Please choose another.");
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot || !(token || accessToken || isFollowUp)) return;
    setConfirming(true);
    try {
      const body = {
        appointment_datetime: selectedSlot.datetime_utc,
        consultation_type: isFollowUp ? "follow_up" : "initial",
      };
      if (isFollowUp) {
        body.followup_token = followupToken;
        body.week_number = parseInt(weekNum) || null;
      } else if (accessToken) {
        body.access_token = accessToken;
      }

      const res = await fetch(`${API}/booking/self-service`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.already_booked) {
          setBookingComplete(data);
          toast.info("You already have a confirmed booking");
        } else {
          setBookingComplete(data);
          toast.success("Your consultation has been booked");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not complete booking");
        if (res.status === 409 || res.status === 400) { setSelectedSlot(null); loadSlots(selectedDate, true); }
      }
    } catch { toast.error("Connection error. Please try again."); }
    finally { setConfirming(false); }
  };

  const scrollDates = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  const formatDay = (d) => d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
  const formatDayNum = (d) => d.getDate();
  const formatMonth = (d) => d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
  const isSelected = (d) => selectedDate && d.toDateString() === selectedDate.toDateString();

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" />
    </div>
  );

  // Follow-up token error
  if (isFollowUp && followupError) return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="consultation-booking-page">
      <Header user={user} />
      <main className="max-w-2xl mx-auto px-5 py-20 text-center space-y-4">
        <Clock className="w-12 h-12 text-stone-400 mx-auto" />
        <h1 className="text-xl font-bold text-stone-900">Booking link unavailable</h1>
        <p className="text-stone-500 text-sm">{followupError}</p>
        <p className="text-xs text-stone-400">If you need help, contact your dietitian or email hello@platemind.app</p>
      </main>
    </div>
  );

  // No personal link (and no session): explain how to book, no sign-in
  if ((!isFollowUp && !accessToken && !user) || accessError) return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="consultation-booking-page">
      <Header />
      <main className="max-w-2xl mx-auto px-5 py-20 text-center space-y-5" data-testid="booking-link-required">
        <CalendarClock className="w-12 h-12 text-[#FF7A00] mx-auto" />
        <h1 className="text-2xl font-bold text-stone-900">{accessError ? "Booking link unavailable" : "Use your personal booking link"}</h1>
        <p className="text-stone-500 text-sm max-w-md mx-auto">{accessError || "After payment, we email you a personal link to book your consultation. Please open the \"Book your consultation\" button in that email."}</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <a href="https://wa.me/2347062039281?text=Hello%20PlateMind%2C%20I%20need%20help%20booking%20my%20consultation." target="_blank" rel="noreferrer" className="inline-block bg-[#FF7A00] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E56E00] transition-colors" data-testid="booking-help-whatsapp">Get help on WhatsApp</a>
          <a href="/programme" className="inline-block border border-stone-300 text-stone-700 px-6 py-3 rounded-full font-semibold hover:border-[#FF7A00] transition-colors" data-testid="booking-see-programme">See the programme</a>
        </div>
        <p className="text-xs text-stone-400">Or email hello@platemind.app</p>
      </main>
    </div>
  );

  // Booking confirmed
  if (bookingComplete) return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="consultation-booking-page">
      <Header user={user} />
      <main className="max-w-2xl mx-auto px-5 py-16 text-center space-y-8">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">You're all set</h1>
          <p className="text-stone-600 max-w-md mx-auto">
            {isFollowUp
              ? `Your follow-up consultation${weekNum ? ` (Week ${weekNum})` : ""} has been booked. We'll send you a confirmation with the meeting details.`
              : "Your consultation has been booked. We'll send you a confirmation email with everything you need to prepare."
            }
          </p>
        </div>
        <Card className="rounded-2xl border-stone-200 p-6 text-left max-w-sm mx-auto space-y-3" data-testid="booking-confirmation-card">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-5 h-5 text-[#FF7A00]" />
            <div>
              <p className="text-sm font-semibold text-stone-900">
                {new Date(bookingComplete.appointment_datetime).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Lagos" })}
              </p>
              <p className="text-xs text-stone-500">
                {new Date(bookingComplete.appointment_datetime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" })} WAT
                {isFollowUp && <span className="ml-1 text-[#FF7A00]">Follow-up consultation</span>}
              </p>
            </div>
          </div>
          {bookingComplete.meeting_url && (
            <a href={bookingComplete.meeting_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#FF7A00] text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#E56E00] transition-colors w-full justify-center" data-testid="join-meeting-btn">
              <Video className="w-4 h-4" /> Join Meeting <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </Card>
        {!isFollowUp && (
          <p className="text-sm text-stone-400 max-w-md mx-auto">
            We'll send you a short pre-consultation form to complete before your session.
          </p>
        )}
      </main>
    </div>
  );

  // Already has a booking
  if (access?.has_existing_booking && access.existing_booking) return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="consultation-booking-page">
      <Header user={user} />
      <main className="max-w-2xl mx-auto px-5 py-16 text-center space-y-8">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Your consultation is booked</h1>
        <Card className="rounded-2xl border-stone-200 p-6 text-left max-w-sm mx-auto space-y-3" data-testid="existing-booking-card">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-5 h-5 text-[#FF7A00]" />
            <div>
              <p className="text-sm font-semibold text-stone-900">
                {new Date(access.existing_booking.appointment_datetime).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Lagos" })}
              </p>
              <p className="text-xs text-stone-500">
                {new Date(access.existing_booking.appointment_datetime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" })} WAT
              </p>
            </div>
          </div>
          {access.existing_booking.meeting_url && (
            <a href={access.existing_booking.meeting_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#FF7A00] text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#E56E00] transition-colors w-full justify-center">
              <Video className="w-4 h-4" /> Join Meeting <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </Card>
      </main>
    </div>
  );

  // Determine titles based on mode
  const useZohoEmbed = zohoConfig?.configured && zohoConfig?.zoho_bookings_url;
  const title = isFollowUp ? "Book a follow-up consultation" : "Book your consultation";
  const subtitle = isFollowUp
    ? `${followupContext?.programme_week || ""} - Choose a time to speak with your dietitian.`
    : "Choose a time that works for you. Your 45-minute video consultation will be with a registered dietitian.";

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="consultation-booking-page">
      <Header user={user} />
      <main className="max-w-3xl mx-auto px-5 py-10">
        {/* Title */}
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-[#FF7A00] uppercase tracking-widest mb-3">
            {isFollowUp ? "Follow-up" : "Consultation"}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">{title}</h1>
          <p className="text-stone-500 text-sm max-w-md mx-auto">{subtitle}</p>
          {isFollowUp && followupContext?.patient_name && (
            <p className="text-xs text-stone-400 mt-2">Hi {followupContext.patient_name}, your follow-up is included in your programme.</p>
          )}
        </div>

        {useZohoEmbed ? (
          <Card className="rounded-2xl border-stone-200 overflow-hidden shadow-sm" data-testid="zoho-booking-embed">
            <iframe src={zohoConfig.zoho_bookings_url} style={{ width: "100%", height: "650px", border: "none" }} title="Book your consultation" />
          </Card>
        ) : (
          <Card className="rounded-2xl border-stone-200 shadow-sm overflow-hidden" data-testid="booking-widget">
            {/* Steps */}
            <div className="bg-stone-50 px-6 py-4 border-b border-stone-100">
              <div className="flex items-center gap-6 text-sm">
                <Step num="1" label="Choose a time" active={!selectedSlot} />
                <div className="w-6 h-px bg-stone-300" />
                <Step num="2" label="Confirm" active={!!selectedSlot} />
              </div>
            </div>

            {/* Date Picker */}
            <div className="px-4 py-5 border-b border-stone-100">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3 px-2">Pick a day</p>
              <div className="relative">
                <button onClick={() => scrollDates(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-stone-50">
                  <ChevronLeft className="w-4 h-4 text-stone-600" />
                </button>
                <div ref={scrollRef} className="flex gap-2 overflow-x-auto scrollbar-hide px-10 py-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                  {dates.map((dt, i) => (
                    <button key={i} onClick={() => setSelectedDate(dt)}
                      className={`flex flex-col items-center justify-center min-w-[64px] h-[76px] rounded-xl transition-all shrink-0 ${
                        isSelected(dt) ? "bg-[#FF7A00] text-white shadow-md" : "bg-white border border-stone-200 text-stone-700 hover:border-[#FF7A00]/40 hover:bg-[#FFF7F0]"
                      }`} data-testid={`date-${dt.toISOString().split("T")[0]}`}>
                      <span className={`text-[10px] font-bold tracking-wider ${isSelected(dt) ? "text-white/80" : "text-stone-400"}`}>{formatDay(dt)}</span>
                      <span className="text-lg font-bold leading-tight">{formatDayNum(dt)}</span>
                      <span className={`text-[10px] font-medium ${isSelected(dt) ? "text-white/80" : "text-stone-400"}`}>{formatMonth(dt)}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => scrollDates(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-stone-50">
                  <ChevronRight className="w-4 h-4 text-stone-600" />
                </button>
              </div>
            </div>

            {/* Time Slots */}
            <div className="px-6 py-5">
              {slotsLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>
              ) : slots.filter(s => s.available).length > 0 ? (
                <>
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                    {selectedDate?.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} - Available times
                  </p>
                  <p className="text-xs text-stone-400 -mt-2 mb-3" data-testid="timezone-label">All times in {win?.timezone_label || "West Africa Time (WAT, UTC+1)"}. Each consultation lasts {win?.duration_minutes || 45} minutes.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                    {slots.filter(s => s.available).map(slot => (
                      <button key={slot.time} onClick={() => pickSlot(slot)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          selectedSlot?.time === slot.time ? "bg-[#FF7A00] text-white shadow-sm" : "bg-white border border-stone-200 text-stone-700 hover:border-[#FF7A00] hover:text-[#FF7A00]"
                        }`} data-testid={`slot-${slot.time}`}>
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-500" data-testid="no-slots-message">{slotsMessage || "No times available on this day. Try another."}</p>
                </div>
              )}
            </div>

            {/* Confirm */}
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-100">
              {selectedSlot ? (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-sm text-stone-600">
                    <span className="font-semibold text-stone-900">
                      {selectedDate?.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                    </span>{" at "}<span className="font-semibold text-[#FF7A00]">{selectedSlot.time} WAT</span>
                    <span className="text-stone-400 ml-2">45 min, video call</span>
                  </div>
                  <Button onClick={handleConfirmBooking} disabled={confirming}
                    className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl px-8 py-2.5 font-semibold" data-testid="confirm-booking-btn">
                    {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Confirm booking <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-stone-400 text-center">Select a date and time to continue</p>
              )}
            </div>
          </Card>
        )}

        {/* Info cards */}
        <div className="mt-10 grid sm:grid-cols-3 gap-5">
          <InfoCard icon={CalendarClock} title="45 minutes" desc="One-on-one video consultation with your dietitian." />
          <InfoCard icon={Video} title="Video call" desc="A Google Meet link will be sent to you after booking." />
          <InfoCard icon={CheckCircle2} title={isFollowUp ? "No extra cost" : "Preparation"}
            desc={isFollowUp ? "Follow-up consultations are included in your programme." : "We'll send a short form to complete before your session."} />
        </div>
      </main>
    </div>
  );
};

const Header = ({ user }) => (
  <header className="bg-white border-b border-stone-100">
    <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
      <a href="/"><img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto" /></a>
      {user ? <span className="text-xs text-stone-500">{user.name}</span> : null}
    </div>
  </header>
);

const Step = ({ num, label, active }) => (
  <div className={`flex items-center gap-2 ${active ? "text-[#FF7A00]" : "text-stone-400"}`}>
    <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${active ? "bg-[#FF7A00] text-white" : "bg-stone-200 text-stone-500"}`}>{num}</span>
    <span className="text-sm font-medium">{label}</span>
  </div>
);

const InfoCard = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-100">
    <Icon className="w-5 h-5 text-[#FF7A00] shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-semibold text-stone-900">{title}</p>
      <p className="text-xs text-stone-500 mt-0.5">{desc}</p>
    </div>
  </div>
);

export default ConsultationBooking;
